import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import moment from "moment-timezone";

import breakpoints from "_constants/breakpoints";
import { getProcessingSpeed, getOrderStatus, getMainProduct, handleProductStatus, getLogOfStatuses } from "_helpers";

import { ReactComponent as CalendarSVG } from "assets/img/Calendar.svg";

import Title from "containers/Title";

const OrderContainer = styled.div`
  width: 100%;
  padding: 6px 18px;
  border-bottom: 1px solid #ecedf1;
  ${({ index }) =>
    index % 2 !== 0 &&
    css`
      background-color: #fafaff;
    `}

  @media (max-width: ${breakpoints.mobileM}) {
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 18px 18px;
  }
`;
const StyledTitle = styled(Title)`
  @media (max-width: ${breakpoints.mobileM}) {
    font-size: 18px;
  }
`;
const DataRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;

  @media (max-width: ${breakpoints.mobileM}) {
    flex-direction: column;
  }
`;
const DataCell = styled.div`
  min-width: 50%;
  text-align: left;
  font-style: normal;
  font-weight: ${({ bold }) => (bold ? "bold" : "400")};
  font-size: 12px;
  color: ${({ color }) => color || "#394149"};

  @media (max-width: ${breakpoints.mobileM}) {
    min-width: 100%;
  }
`;
const DateCell = styled(DataCell)`
  text-transform: uppercase;
`;
const Status = styled.div`
  width: 100%;
  padding: 0 7px;
  color: #44cdc8;
  background: #e3f8f7;
  border: 1px solid #44cdc8;
  box-sizing: border-box;
  border-radius: 3px;
  text-transform: uppercase;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  text-align: center;

  ${({ status }) =>
    status === "Cancelled" &&
    css`
      color: #ef9a9a;
      background: #ffebee;
      border: 1px solid #ef9a9a;
    `}

  ${({ status }) =>
    status === "Error" &&
    css`
      color: #efc09a;
      background: #fff5eb;
      border: 1px solid #efc09a;
    `}
`;

function Order(props) {
  const navigate = useNavigate();

  const { processingStatusesObj } = useSelector(({ constant }) => constant);

  const { scrollToContent } = props;

  const mainProduct = getMainProduct(props.products);
  const productStatus = getOrderStatus(props.products);
  const logOfStatuses = getLogOfStatuses(props.products);
  const { status: handledStatus } = handleProductStatus({ status: productStatus, logOfStatuses });
  const status = (processingStatusesObj && processingStatusesObj[handledStatus]) || handledStatus || "N/A";

  const handleOpenOrder = () => {
    scrollToContent();
    navigate(`/orders/${props.uid}`);
  };

  return (
    <OrderContainer index={props.index} onClick={handleOpenOrder}>
      <DataRow>
        <DataCell color="#03589C" bold>
          {props.crmOrderId}
        </DataCell>
        <DataCell>
          <Status status={status}>{status}</Status>
        </DataCell>
      </DataRow>
      <DataCell>{props.productCodes.map(({ name }) => name).join(", ")}</DataCell>
      <DataRow>
        <DateCell>
          <CalendarSVG /> {moment(mainProduct.paymentDate, "YYYY-MM-DDThh:mm:ss").format("MMM DD, YYYY")}
        </DateCell>
        <DataCell>{getProcessingSpeed(props.products)}</DataCell>
      </DataRow>
    </OrderContainer>
  );
}

Order.propTypes = {
  uid: PropTypes.string.isRequired,
  crmOrderId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  products: PropTypes.array.isRequired,
  productCodes: PropTypes.array.isRequired,
  scrollToContent: PropTypes.func,
};

function OrderTableMobile({ orderToShow, scrollToContent }) {
  return (
    <>
      <StyledTitle>Orders</StyledTitle>
      {orderToShow.map((order, idx) => (
        <Order key={idx} index={idx} {...order} scrollToContent={scrollToContent} />
      ))}
      <div style={{ height: "48px" }} />
    </>
  );
}

OrderTableMobile.propTypes = {
  orderToShow: PropTypes.arrayOf(PropTypes.object),
  scrollToContent: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onCurrentPageChange: PropTypes.func,
};

export default OrderTableMobile;
